import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cafeData } from '../data/cafeData';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center bg-[#3A4D39]" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#3A4D39] via-[#2C3B2B] to-[#1a2419]" />
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 2px, transparent 2px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 80px 80px'
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[#E07A5F] text-sm font-medium uppercase tracking-widest mb-4">
              South Wimbledon · Since 2022
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Authentic Italian Flavours in Your Neighbourhood
            </h1>
            <p className="text-white/80 text-lg md:text-xl mb-8 leading-relaxed">
              A family-run Italian café bringing Calabrian warmth, fresh pastries and homemade dishes to 110 Haydons Road.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/menu" data-testid="hero-view-menu-btn">
                <Button className="bg-[#E07A5F] hover:bg-[#d06a4f] text-white px-8 py-6 text-base font-medium tracking-wide">
                  View Menu
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-visit-us-btn">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3A4D39] px-8 py-6 text-base font-medium">
                  Visit Us
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-6 mt-10">
              <div className="flex items-center gap-2" data-testid="hero-hygiene-badge">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                  {cafeData.hygiene.rating}
                </div>
                <span className="text-white/80 text-sm">Food Hygiene Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-white/80 text-sm">{cafeData.reviews.averageRating} ({cafeData.reviews.totalReviews} reviews)</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Dishes Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFBF7]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Our Specialties</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] mt-4">
              Popular Dishes
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cafeData.featuredDishes.map((dish, index) => (
              <motion.div
                key={dish.name}
                className="bg-white border border-stone-100 hover:border-stone-300 transition-all duration-300 overflow-hidden group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`featured-dish-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={dish.image}
                    alt={dish.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[#E07A5F] text-xs font-medium uppercase tracking-widest">{dish.category}</span>
                  <h3 className="font-serif text-xl font-bold text-[#1C1917] mt-2">{dish.name}</h3>
                  <p className="text-stone-500 text-sm mt-2 line-clamp-2">{dish.description}</p>
                  <p className="text-[#3A4D39] font-bold text-lg mt-4">{dish.price}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/menu" data-testid="view-full-menu-btn">
              <Button className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white px-8 py-4 text-base font-medium tracking-wide">
                View Full Menu
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Why Choose Us</span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] mt-4 mb-8">
                A Taste of Calabria in Wimbledon
              </h2>
              
              <div className="space-y-6">
                {[
                  { title: 'Family Run', desc: 'A family café from Calabria, Italy, serving our neighbourhood since 2022' },
                  { title: 'Fresh Daily', desc: 'Pastries baked fresh every morning' },
                  { title: 'Authentic Recipes', desc: 'Traditional Italian methods and ingredients from home' },
                  { title: '5-Star Hygiene', desc: 'Top food safety standards maintained' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-medium text-[#1C1917]">{item.title}</h4>
                      <p className="text-stone-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="w-full aspect-[4/3] bg-[#FDFBF7] border border-stone-100 flex items-center justify-center p-16">
                <img 
                  src={cafeData.logo} 
                  alt="Cafe 110 Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#3A4D39] text-white p-8 hidden md:block">
                <p className="font-serif text-4xl font-bold">{cafeData.reviews.averageRating}</p>
                <p className="text-white/80 text-sm mt-1">Average Rating</p>
                <div className="flex mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Preview Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFBF7]" data-testid="reviews-preview-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Testimonials</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] mt-4">
              What Our Customers Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cafeData.reviews.highlights.slice(0, 3).map((review, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 border border-stone-100 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`review-preview-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-stone-600 italic leading-relaxed mb-6">"{review.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#3A4D39] flex items-center justify-center text-white font-medium">
                    {review.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#1C1917]">{review.author}</p>
                    <p className="text-stone-400 text-sm">{review.date}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews" data-testid="see-all-reviews-btn">
              <Button variant="outline" className="border-[#3A4D39] text-[#3A4D39] hover:bg-[#3A4D39] hover:text-white px-8 py-4">
                See All Reviews
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="w-full h-80 md:h-96 border border-stone-200 overflow-hidden" data-testid="google-map-embed">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2489.8!2d${cafeData.coordinates.lng}!3d${cafeData.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDI1JzEzLjEiTiAwwrAxMScxNC4wIlc!5e0!3m2!1sen!2suk!4v1635000000000!5m2!1sen!2suk`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Cafe 110 Location"
                />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Visit Us</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1917] mt-4 mb-8">
                Find Us in South Wimbledon
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#3A4D39] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-[#1C1917] mb-1">Address</h4>
                    <p className="text-stone-500">{cafeData.address.full}</p>
                    <p className="text-stone-400 text-sm mt-1">{cafeData.distanceFromStation} from {cafeData.nearestStation} Station</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-[#3A4D39] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-[#1C1917] mb-2">Opening Hours</h4>
                    <div className="space-y-1">
                      {cafeData.hours.map((item) => (
                        <div key={item.day} className="flex justify-between text-sm text-stone-500 w-48">
                          <span>{item.day}</span>
                          <span>{item.open} - {item.close}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <a 
                  href={cafeData.googleMapsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center justify-center bg-[#3A4D39] hover:bg-[#2C3B2B] text-white px-6 py-3 font-medium rounded-md transition-colors"
                  data-testid="get-directions-btn"
                >
                  Get Directions
                </a>
                <Link to="/contact" data-testid="contact-us-btn">
                  <Button variant="outline" className="border-[#3A4D39] text-[#3A4D39] hover:bg-[#3A4D39] hover:text-white px-6 py-3">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#3A4D39]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Come Visit Us
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Stop by for a coffee, grab a fresh pastry, or enjoy a proper Italian lunch at 110 Haydons Road.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu" data-testid="cta-view-menu-btn">
                <Button className="bg-[#E07A5F] hover:bg-[#d06a4f] text-white px-10 py-6 text-lg font-medium">
                  View Our Menu
                </Button>
              </Link>
              <Link to="/about" data-testid="cta-our-story-btn">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3A4D39] px-10 py-6 text-lg">
                  Our Story
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
